import React, { useEffect, useMemo, useState } from 'react';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../components/TopNavigation';
import { ResolvedImg } from '../components/AdFormFields';
import SignedVideo from '../components/SignedVideo';
import { formatCurrency } from '../utils/currency';
import { ChartBarIcon, PencilIcon, PauseIcon, PlayIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { getWallet } from '../utils/wallet';
import WalletTopupPayPal from '../components/WalletTopupPayPal';
import { deleteAd, listMyAds, updateAd } from '../utils/secureFunctions';
import { appConfirm } from '../utils/dialog';

interface AdDoc {
  id: string;
  title: string;
  description: string;
  imageURL?: string;
  videoURL?: string;
  targetURL: string;
  status: 'active' | 'paused' | 'pending_approval' | 'completed' | 'rejected';
  reviewStatus?: string;
  budget: number;
  budgetSpent: number;
  impressions: number;
  clicks: number;
  conversions: number;
  costPerImpression: number;
  costPerClick: number;
  createdAt: any;
  updatedAt: any;
  paymentStatus?: string;
  aiScan?: { result: string; reasons?: string[] };
  pageId?: string;
  placements?: string[];
}

const AdvertiserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [ads, setAds] = useState<AdDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'paused' | 'pending'>('all');
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [topupAmount, setTopupAmount] = useState<number>(20);
  const [showPayPalTopup, setShowPayPalTopup] = useState<boolean>(false);
  const [topupKey, setTopupKey] = useState(0);
  const uid = auth.currentUser?.uid;

  const loadAds = async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const res = await listMyAds();
      const docs = (res.ads || []).map((a: any) => ({ id: a.id, ...a } as AdDoc));
      setAds(docs);
    } catch (error) {
      console.error('Failed to load ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWallet = async () => {
    if (!uid) return;
    try {
      const w = await getWallet(uid);
      setWalletBalance(Number(w?.balanceUSD || 0));
    } catch (e) {
      console.warn('Failed to load wallet');
    }
  };

  useEffect(() => {
    if (!uid) return;
    loadAds();
    loadWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const handlePause = async (adId: string) => {
    try {
      await updateAd(adId, { status: 'paused' });
      loadAds();
    } catch (error) {
      console.error('Failed to pause ad:', error);
      alert('Failed to pause ad');
    }
  };

  const handleResume = async (adId: string) => {
    try {
      await updateAd(adId, { status: 'active' });
      loadAds();
    } catch (error) {
      console.error('Failed to resume ad:', error);
      alert('Failed to resume ad');
    }
  };

  const handleDelete = async (adId: string) => {
    if (!await appConfirm('Delete this ad? This action cannot be undone.')) return;
    try {
      await deleteAd(adId);
      loadAds();
    } catch (error) {
      console.error('Failed to delete ad:', error);
      alert('Failed to delete ad');
    }
  };

  const filteredAds = ads.filter(ad => {
    if (filter === 'all') return true;
    if (filter === 'active') return ad.status === 'active';
    if (filter === 'paused') return ad.status === 'paused';
    if (filter === 'pending') return ad.status === 'pending_approval';
    return true;
  });

  const totalSpent = ads.reduce((sum, ad) => sum + (ad.budgetSpent || 0), 0);
  const totalImpressions = ads.reduce((sum, ad) => sum + (ad.impressions || 0), 0);
  const totalClicks = ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0);
  const avgCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
  const walletDisplay = useMemo(() => formatCurrency(walletBalance, 'USD'), [walletBalance]);

  return (
    <div style={styles.container}>
      <TopNavigation />
      <div style={styles.content}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>My Advertisements</h1>
            <p style={styles.subtitle}>Manage and track your ad campaigns</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate('/conversions')} 
              style={{ ...styles.createBtn, backgroundColor: '#2196f3' }}
            >
              📊 Conversions
            </button>
            <button onClick={() => navigate('/create-ad')} style={styles.createBtn}>
              + Create New Ad
            </button>
          </div>
        </div>

        {/* Wallet + Summary */}
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>🏦</div>
            <div>
              <div style={styles.summaryLabel}>Wallet Balance</div>
              <div style={styles.summaryValue}>{walletDisplay}</div>
              <div style={{ marginTop: 5, marginBottom: 10 }}>
                <button
                  onClick={() => navigate('/wallet-transactions')}
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    backgroundColor: 'transparent',
                    color: 'var(--color-primary)',
                    border: '1px solid var(--color-primary)',
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}
                >
                  View Transactions
                </button>
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="number"
                  min={5}
                  step={5}
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(Math.max(0, Number(e.target.value)))}
                  style={{ padding: 8, borderRadius: 6, border: '1px solid var(--color-border)', width: 120 }}
                />
                <button
                  onClick={() => { if (topupAmount >= 1) { setTopupKey((k) => k + 1); setShowPayPalTopup(true); } else { alert('Enter an amount of at least $1'); } }}
                  style={styles.topupBtn}
                >
                  Fund wallet with PayPal
                </button>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 6 }}>
                Pay securely with PayPal or a debit/credit card. Your wallet is credited once PayPal confirms the payment.
              </div>
              {showPayPalTopup && (
                <WalletTopupPayPal
                  key={topupKey}
                  amount={topupAmount}
                  onCredited={loadWallet}
                  onClose={() => setShowPayPalTopup(false)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>💰</div>
            <div>
              <div style={styles.summaryLabel}>Total Spent</div>
              <div style={styles.summaryValue}>{formatCurrency(totalSpent, 'USD')}</div>
            </div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>👁️</div>
            <div>
              <div style={styles.summaryLabel}>Total Impressions</div>
              <div style={styles.summaryValue}>{totalImpressions.toLocaleString()}</div>
            </div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>🖱️</div>
            <div>
              <div style={styles.summaryLabel}>Total Clicks</div>
              <div style={styles.summaryValue}>{totalClicks.toLocaleString()}</div>
            </div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>📊</div>
            <div>
              <div style={styles.summaryLabel}>Avg CTR</div>
              <div style={styles.summaryValue}>{avgCTR}%</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={styles.filters}>
          <button
            onClick={() => setFilter('all')}
            style={{ ...styles.filterBtn, ...(filter === 'all' ? styles.filterBtnActive : {}) }}
          >
            All ({ads.length})
          </button>
          <button
            onClick={() => setFilter('active')}
            style={{ ...styles.filterBtn, ...(filter === 'active' ? styles.filterBtnActive : {}) }}
          >
            Active ({ads.filter(a => a.status === 'active').length})
          </button>
          <button
            onClick={() => setFilter('paused')}
            style={{ ...styles.filterBtn, ...(filter === 'paused' ? styles.filterBtnActive : {}) }}
          >
            Paused ({ads.filter(a => a.status === 'paused').length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            style={{ ...styles.filterBtn, ...(filter === 'pending' ? styles.filterBtnActive : {}) }}
          >
            Pending ({ads.filter(a => a.status === 'pending_approval').length})
          </button>
        </div>

        {/* Ads List */}
        {loading ? (
          <div style={styles.loading}>Loading your ads...</div>
        ) : filteredAds.length === 0 ? (
          <div style={styles.empty}>
            <ChartBarIcon style={{ width: 64, height: 64, color: '#ccc', margin: '0 auto 16px' }} />
            <p style={{ fontSize: 18, color: '#666', marginBottom: 8 }}>No ads found</p>
            <p style={{ fontSize: 14, color: '#999', marginBottom: 20 }}>Create your first ad to start reaching your audience</p>
            <button onClick={() => navigate('/create-ad')} style={styles.createBtn}>
              Create Your First Ad
            </button>
          </div>
        ) : (
          <div style={styles.adsList}>
            {filteredAds.map(ad => (
              <div key={ad.id} style={styles.adCard}>
                <div style={styles.adMedia}>
                  {ad.imageURL ? (
                    <ResolvedImg src={ad.imageURL} alt="" style={styles.adImage} />
                  ) : ad.videoURL ? (
                    <SignedVideo src={ad.videoURL} style={styles.adImage} />
                  ) : (
                    <div style={{ ...styles.adImage, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      📷
                    </div>
                  )}
                </div>
                <div style={styles.adDetails}>
                  <div style={styles.adHeader}>
                    <div>
                      <h3 style={styles.adTitle}>{ad.title}</h3>
                      <p style={styles.adDescription}>{ad.description}</p>
                    </div>
                    <div style={styles.statusBadge} data-status={ad.status}>
                      {ad.status.replace('_', ' ')}
                    </div>
                  </div>

                  <div style={styles.adMetrics}>
                    <div style={styles.metric}>
                      <span style={styles.metricLabel}>Budget</span>
                      <span style={styles.metricValue}>{formatCurrency(ad.budget, 'USD')}</span>
                    </div>
                    <div style={styles.metric}>
                      <span style={styles.metricLabel}>Spent</span>
                      <span style={styles.metricValue}>{formatCurrency(ad.budgetSpent || 0, 'USD')}</span>
                    </div>
                    <div style={styles.metric}>
                      <span style={styles.metricLabel}>Impressions</span>
                      <span style={styles.metricValue}>{(ad.impressions || 0).toLocaleString()}</span>
                    </div>
                    <div style={styles.metric}>
                      <span style={styles.metricLabel}>Clicks</span>
                      <span style={styles.metricValue}>{(ad.clicks || 0).toLocaleString()}</span>
                    </div>
                    <div style={styles.metric}>
                      <span style={styles.metricLabel}>CTR</span>
                      <span style={styles.metricValue}>
                        {ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : '0.00'}%
                      </span>
                    </div>
                    <div style={styles.metric}>
                      <span style={styles.metricLabel}>Conversions</span>
                      <span style={styles.metricValue}>{ad.conversions || 0}</span>
                    </div>
                  </div>

                  {ad.aiScan && ad.aiScan.result !== 'clean' && (
                    <div style={styles.aiWarning}>
                      ⚠️ AI Scan: {ad.aiScan.result} {ad.aiScan.reasons && `(${ad.aiScan.reasons.join(', ')})`}
                    </div>
                  )}

                  <div style={styles.adActions}>
                    <button
                      onClick={() => navigate(`/edit-ad/${ad.id}`)}
                      style={styles.actionBtn}
                      title="Edit"
                    >
                      <PencilIcon style={{ width: 16, height: 16 }} />
                      Edit
                    </button>
                    {ad.status === 'active' ? (
                      <button
                        onClick={() => handlePause(ad.id)}
                        style={styles.actionBtn}
                        title="Pause"
                      >
                        <PauseIcon style={{ width: 16, height: 16 }} />
                        Pause
                      </button>
                    ) : ad.status === 'paused' ? (
                      <button
                        onClick={() => handleResume(ad.id)}
                        style={styles.actionBtn}
                        title="Resume"
                      >
                        <PlayIcon style={{ width: 16, height: 16 }} />
                        Resume
                      </button>
                    ) : null}
                    <button
                      onClick={() => window.open(ad.targetURL, '_blank')}
                      style={styles.actionBtn}
                      title="View Target"
                    >
                      <EyeIcon style={{ width: 16, height: 16 }} />
                      View
                    </button>
                    <button
                      onClick={() => handleDelete(ad.id)}
                      style={{ ...styles.actionBtn, color: '#d32f2f' }}
                      title="Delete"
                    >
                      <TrashIcon style={{ width: 16, height: 16 }} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: 'var(--color-background-alt)',
  },
  content: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: 'clamp(12px, 4vw, 20px)',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: 'var(--color-text)',
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: 'var(--color-text-muted)',
    marginTop: 4,
  },
  createBtn: {
    padding: '12px 20px',
    background: 'var(--color-primary-600)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: '600',
    transition: 'background 0.2s',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  summaryCard: {
    background: 'var(--color-surface)',
    borderRadius: 12,
    padding: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    minWidth: 0,
    boxSizing: 'border-box',
    border: '1px solid var(--color-border)',
  },
  summaryIcon: {
    fontSize: 32,
  },
  summaryLabel: {
    fontSize: 13,
    color: 'var(--color-text-muted)',
    marginBottom: 4,
  },
  summaryValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'var(--color-text)',
  },
  topupBtn: {
    padding: '8px 16px',
    background: 'var(--color-primary-600)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  filters: {
    display: 'flex',
    gap: 8,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  filterBtn: {
    padding: '8px 16px',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    color: 'var(--color-text)',
    transition: 'all 0.2s',
  },
  filterBtnActive: {
    background: 'var(--color-primary-600)',
    color: '#fff',
    borderColor: 'var(--color-primary-600)',
  },
  loading: {
    textAlign: 'center',
    padding: 40,
    color: 'var(--color-text-muted)',
  },
  empty: {
    textAlign: 'center',
    padding: 60,
    background: 'var(--color-surface)',
    borderRadius: 12,
    border: '1px solid var(--color-border)',
  },
  adsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  adCard: {
    background: 'var(--color-surface)',
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    border: '1px solid var(--color-border)',
    transition: 'box-shadow 0.2s',
  },
  adMedia: {
    flexShrink: 0,
    width: 160,
    height: 120,
  },
  adImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 8,
  },
  adDetails: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  adHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  adTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: 'var(--color-text)',
    margin: 0,
  },
  adDescription: {
    fontSize: 14,
    color: 'var(--color-text-muted)',
    margin: '4px 0 0',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  },
  adMetrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
    gap: 12,
  },
  metric: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  metricLabel: {
    fontSize: 12,
    color: 'var(--color-text-muted)',
  },
  metricValue: {
    fontSize: 16,
    fontWeight: '600',
    color: 'var(--color-text)',
  },
  aiWarning: {
    padding: 8,
    background: '#fff3cd',
    color: '#856404',
    borderRadius: 6,
    fontSize: 13,
  },
  adActions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  actionBtn: {
    padding: '6px 12px',
    background: 'var(--color-background-alt)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    color: 'var(--color-text)',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.2s',
  },
};

// Add dynamic status badge colors
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  [data-status="active"] { background: #d4edda; color: #155724; }
  [data-status="paused"] { background: #fff3cd; color: #856404; }
  [data-status="pending_approval"] { background: #cce5ff; color: #004085; }
  [data-status="rejected"] { background: #f8d7da; color: #721c24; }
  [data-status="completed"] { background: #e2e3e5; color: #383d41; }
`;
document.head.appendChild(styleSheet);

export default AdvertiserDashboard;
